import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import Reveal from "@/components/Reveal";

const TARGET = new Date("2026-11-15T00:00:00");

const getRemaining = () => {
  const diff = Math.max(0, TARGET.getTime() - Date.now());
  return {
    days: Math.floor(diff / 86_400_000),
    hours: Math.floor((diff / 3_600_000) % 24),
    minutes: Math.floor((diff / 60_000) % 60),
    seconds: Math.floor((diff / 1_000) % 60),
  };
};

const Unit = ({ value, label, testid }) => (
  <div className="flex w-16 flex-col items-center md:w-28">
    <div className="flex h-16 w-full items-center justify-center overflow-hidden rounded-2xl border border-pine/15 bg-paper md:h-24">
      <motion.span
        key={value}
        data-testid={testid}
        initial={{ y: 14, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="font-serif text-3xl font-light tabular-nums text-pine md:text-5xl"
      >
        {String(value).padStart(2, "0")}
      </motion.span>
    </div>
    <p className="mt-3 text-[9px] font-semibold uppercase tracking-[0.18em] text-pine/55 md:text-[11px] md:tracking-[0.22em]">
      {label}
    </p>
  </div>
);

export default function Establishments() {
  const { t } = useLang();
  const e = t.establishments;
  const [remaining, setRemaining] = useState(getRemaining);

  useEffect(() => {
    const timer = setInterval(() => setRemaining(getRemaining()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section
      id="etablissements"
      data-testid="establishments-section"
      className="scroll-mt-24 border-y border-pine/10 bg-cream py-24 md:py-36"
    >
      <div className="mx-auto max-w-4xl px-5 text-center md:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-wood">{e.eyebrow}</p>
          <h2
            data-testid="establishments-teaser"
            className="mt-6 font-serif text-4xl font-light italic leading-tight tracking-tight text-pine md:text-6xl"
          >
            {e.line}
          </h2>
          <div className="mt-10 flex items-center justify-center gap-4">
            <span className="h-px w-12 bg-wood/50" />
            <p className="flex items-center gap-1 text-sm font-semibold uppercase tracking-[0.32em] text-pine/70">
              {e.sub}
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  className="inline-block h-1 w-1 rounded-full bg-wood"
                  animate={{ opacity: [0.2, 1, 0.2] }}
                  transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.3 }}
                />
              ))}
            </p>
            <span className="h-px w-12 bg-wood/50" />
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div data-testid="countdown" className="mt-14 flex items-start justify-center gap-2 md:gap-6">
            <Unit value={remaining.days} label={e.units.days} testid="countdown-days" />
            <span className="mt-5 font-serif text-2xl text-wood/70 md:mt-8 md:text-3xl">:</span>
            <Unit value={remaining.hours} label={e.units.hours} testid="countdown-hours" />
            <span className="mt-5 font-serif text-2xl text-wood/70 md:mt-8 md:text-3xl">:</span>
            <Unit value={remaining.minutes} label={e.units.minutes} testid="countdown-minutes" />
            <span className="mt-5 font-serif text-2xl text-wood/70 md:mt-8 md:text-3xl">:</span>
            <Unit value={remaining.seconds} label={e.units.seconds} testid="countdown-seconds" />
          </div>
          <p data-testid="opening-date" className="mt-10 text-xs font-semibold uppercase tracking-[0.24em] text-wood">
            {e.date}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
